// src/lib/compta-api.js — client de l'endpoint /api/compta (Lot 2).
//
// Lot 2 : le domaine COMPTA est passé du KV fbz8-compta à la table single-row
// compta_config, derrière un endpoint serveur admin-only. GET lit le blob,
// POST écrit le blob entier (et renvoie le blob persisté). Erreurs serveur
// remontées (message FR) pour affichage en toast.
//
// ══ LE JETON DE VERSION ═════════════════════════════════════════════════════
//
// Le serveur pose `x-compta-version` sur le GET et le compare au POST (cf.
// `api/compta.js`). Sans un client qui le RENVOIE, cette garde est décorative :
// toutes les écritures tombent dans la branche de tolérance, et deux onglets
// Compta ouverts se réécrasent en silence — exactement ce qu'on prétendait
// avoir fermé. D'où ce câblage, MIROIR EXACT de `src/lib/data-api.js` et
// `src/lib/cfg-api.js` : même problème, même remède, on ne réinvente pas une
// garde qui fonctionne à côté.
//
// ⚠️ POURQUOI UN ÉTAT DE MODULE ET PAS UN PARAMÈTRE. Le jeton pourrait être
// rendu par `fetchCompta` puis repassé à `saveCompta`. Mais l'appelant réel est
// `ComptaTab`, qui reçoit `saveCompta(blob)` en prop depuis `App.jsx` et
// l'appelle à cinq endroits : faire transiter le jeton par l'état React
// signifierait cinq occasions de l'oublier, et un oubli ne se voit pas (il rend
// juste la garde muette). L'état de module est aussi ce qui départage DEUX
// ONGLETS : deux contextes JavaScript, deux jetons — c'est le mécanisme même.

import { supabase } from '../supabase'
import { lireJsonOuRefuser } from './reponse-json'

// La version détenue par CET onglet.
let jetonVersion = ''

/** Lecture — montrée en bandeau (cf. src/lib/reponse-json.js). */
export const MESSAGE_COMPTA_ILLISIBLE =
  "Je n'ai pas pu lire ta comptabilité : ce que le serveur a renvoyé n'en " +
  "était pas une. Les charges et les dépenses peuvent s'afficher vides ou " +
  "incomplètes — rien n'est perdu, recharge la page."

/** Écriture — on ne peut pas dire si c'est passé. */
export const MESSAGE_COMPTA_ECRITURE_ILLISIBLE =
  "Je n'ai pas pu lire la réponse du serveur : je ne sais pas si " +
  "l'enregistrement est passé. Ne réessaie pas à l'aveugle — recharge la " +
  "page pour voir ce qui est vraiment enregistré."

async function authHeaders() {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

// Le verdict « ce n'est pas du JSON » vit dans src/lib/reponse-json.js —
// quatre recopies de cette fonction laissaient passer une page HTML en 200
// pour un blob vide, et vidaient les écrans sans un mot.
async function parseOrThrow(res, fallback, messageIllisible) {
  return lireJsonOuRefuser(res, fallback, messageIllisible)
}

// GET — blob compta { chargesFixes, depensesVar, consommables }.
// MÉMORISE la version lue. Signature inchangée (rend le blob) : les appelants
// ne portent pas le jeton, cf. l'en-tête.
export async function fetchCompta() {
  const res = await fetch('/api/compta', { headers: await authHeaders() })
  const blob = await parseOrThrow(res, 'Lecture compta échouée', MESSAGE_COMPTA_ILLISIBLE)
  jetonVersion = res.headers?.get?.('x-compta-version') || ''
  return blob
}

// POST — écrit le blob entier ; renvoie le blob persisté (re-sync front).
export async function saveCompta(blob) {
  const res = await fetch('/api/compta', {
    method: 'POST',
    headers: { ...(await authHeaders()), 'x-compta-version': jetonVersion },
    body: JSON.stringify(blob || {}),
  })
  // 🔴 NE JAMAIS mémoriser le jeton avant d'avoir vérifié le succès.
  // Le rafraîchir sur un 409 rendrait un SECOND enregistrement gagnant —
  // c'est-à-dire exactement l'écrasement silencieux que cette garde existe pour
  // empêcher. Sur conflit, la seule issue est de recharger la page.
  const persiste = await parseOrThrow(
    res,
    'Enregistrement compta échoué',
    MESSAGE_COMPTA_ECRITURE_ILLISIBLE,
  )
  jetonVersion = res.headers?.get?.('x-compta-version') || ''
  return persiste
}

// Réservé aux tests : la version détenue est un état de module, elle doit
// pouvoir être posée et lue explicitement pour que les tests prouvent quelque
// chose plutôt que de constater un `undefined`.
export const _jeton = {
  lire: () => jetonVersion,
  poser: (v) => { jetonVersion = v || '' },
}

export const comptaApi = { fetchCompta, saveCompta }
